/* eslint-disable no-magic-numbers */
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { SketchPicker } from 'react-color';
import { CustomPopover } from '@futrli/ui-components';
import styles from './color-picker.scss';

interface Props {
  id: string;
  color: string | undefined;
  onChange: (v: string) => void;
  presetColors?: string[];
  showHex?: boolean;
}
export const ColorPicker = ({ id, color, onChange, presetColors, showHex }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selectedColor, setSelectedColor] = useState(color);
  const updateTimerId = useRef(0);
  // Skip the initial mount to avoid firing onChange when the component first renders.
  // Without this, every ColorPicker instance would call onChange 500ms after mounting,
  // which triggers unnecessary report saves in the report builder theme customization panels.
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (updateTimerId.current) {
      clearTimeout(updateTimerId.current);
    }
    updateTimerId.current = (setTimeout(() => {
      onChange(selectedColor || '');
    }, 500) as unknown) as number;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor]);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const triggerId = open ? id : undefined;

  return (
    <>
      <div aria-describedby={triggerId} className={clsx(styles.colorPicker)} onClick={handleClick}>
        <span className={clsx(styles.color)} style={{ backgroundColor: color }}></span>
        {showHex && selectedColor}
      </div>
      <CustomPopover open={open} anchorEl={anchorEl} onClose={handleClose} id={triggerId} style={{ maxHeight: 'auto' }}>
        <SketchPicker
          color={selectedColor}
          onChange={e => setSelectedColor(e.hex)}
          presetColors={presetColors}
          disableAlpha
        />
      </CustomPopover>
    </>
  );
};
